import { gql, useQuery } from '@apollo/client';
import {
  Badge,
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Link,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';

import { formatDuration } from './utils';

type CallEvent = {
  id: number;
  event_type: string;
  participant_label: string | null;
  participant_number: string | null;
  participant_user_name: string | null;
  hunt_group_slug: string | null;
  reason: string | null;
  sip_response_code: string | null;
  occurred_at: string;
};

type CdrParticipant = {
  id: number;
  participant_user_id: string | null;
  participant_number: string | null;
  joined_at: string | null;
  concluded_at: string | null;
  transferred: boolean;
};

const GET_CALL_LIFECYCLE = gql`
  query getCallLifecycle($conferenceId: String, $callId: String) {
    getCallLifecycle(conferenceId: $conferenceId, callId: $callId) {
      call {
        id
        conference_id
        direction
        flow
        outcome
        fromNumber
        calledNumber
        agent_name
        created_at
        callDuration
        recording_url
        customer_name
        hunt_group_name
      }
      participants {
        id
        participant_user_id
        participant_number
        joined_at
        concluded_at
        transferred
      }
      events {
        id
        event_type
        participant_label
        participant_number
        participant_user_name
        hunt_group_slug
        reason
        sip_response_code
        occurred_at
      }
    }
  }
`;

type CallLifecycleDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  conferenceId: string | null;
  callId: string | null;
};

const EVENT_COLOR: Record<string, string> = {
  customer_joined: 'blue',
  agent_dialed: 'purple',
  agent_joined: 'green',
  agent_declined: 'orange',
  participant_left: 'gray',
  call_held: 'yellow',
  call_resumed: 'teal',
  transfer_initiated: 'cyan',
  voicemail_sent: 'orange',
  sip_failure: 'red',
  hunt_group_changed: 'purple',
  conference_created: 'blue',
  conference_ended: 'gray',
  recording_completed: 'green',
};

// Map colorScheme names to actual hex colors for the dot
const DOT_COLOR: Record<string, string> = {
  blue: '#3182CE',
  purple: '#805AD5',
  green: '#38A169',
  orange: '#DD6B20',
  gray: '#718096',
  yellow: '#D69E2E',
  teal: '#319795',
  cyan: '#00B5D8',
  red: '#E53E3E',
};

const formatTime = (ts: string | null | undefined): string => {
  if (ts == null) {
    return '—';
  }
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};

const formatTimeShort = (ts: string | null | undefined): string => {
  if (ts == null) {
    return '—';
  }
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return ts;
  }
};

const getElapsedSeconds = (firstTs: string, ts: string): string => {
  const diff = Math.round((new Date(ts).getTime() - new Date(firstTs).getTime()) / 1000);
  if (diff < 0) {
    return '';
  }
  const mins = Math.floor(diff / 60);
  const secs = diff % 60;
  return mins > 0 ? `T+${mins}m${secs}s` : `T+${secs}s`;
};

const getParticipantDuration = (p: CdrParticipant): string | null => {
  if (p.joined_at == null || p.concluded_at == null) {
    return null;
  }
  const secs = Math.round(
    (new Date(p.concluded_at).getTime() - new Date(p.joined_at).getTime()) / 1000,
  );
  const mins = Math.floor(secs / 60);
  return `${mins}m ${secs % 60}s`;
};

const CallLifecycleDrawer = ({
  isOpen,
  onClose,
  conferenceId,
  callId,
}: CallLifecycleDrawerProps) => {
  const { data, loading } = useQuery(GET_CALL_LIFECYCLE, {
    variables: {
      conferenceId: conferenceId ?? undefined,
      callId: callId ?? undefined,
    },
    skip: !isOpen || (conferenceId == null && callId == null),
  });

  const lifecycle = data?.getCallLifecycle;
  const call = lifecycle?.call;
  const events = lifecycle?.events ?? [];
  const participants = lifecycle?.participants ?? [];

  const firstEventTs = (events as CallEvent[])[0]?.occurred_at;

  const buildNarrativeSubtitle = (c: typeof call): string => {
    if (c == null) {
      return '—';
    }
    const callerLabel = c.customer_name ?? c.fromNumber ?? '—';
    const calledLabel = c.hunt_group_name ?? c.agent_name ?? c.calledNumber ?? '—';
    if (c.direction === 'outbound') {
      return `${c.agent_name ?? c.fromNumber ?? '—'} called ${
        c.customer_name ?? c.calledNumber ?? '—'
      }`;
    }
    return `${callerLabel} called ${calledLabel}`;
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          {loading ? (
            <Skeleton height="24px" width="200px" />
          ) : (
            <Box>
              <Text fontWeight="bold" fontSize="lg">
                Call Lifecycle
              </Text>
              <Text fontSize="sm" color="gray.500">
                {buildNarrativeSubtitle(call)}
              </Text>
            </Box>
          )}
        </DrawerHeader>

        <DrawerBody>
          {loading ? (
            <VStack spacing={3} mt={4}>
              {(['a', 'b', 'c', 'd', 'e'] as const).map((k) => (
                <Skeleton key={k} height="40px" width="100%" />
              ))}
            </VStack>
          ) : (
            <Box>
              {/* Call Summary */}
              <SimpleGrid columns={2} spacing={3} mb={4} p={3} bg="gray.50" borderRadius="md">
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Outcome
                  </Text>
                  <Badge
                    colorScheme={
                      call?.outcome === 'answered'
                        ? 'green'
                        : call?.outcome === 'voicemail'
                        ? 'yellow'
                        : 'gray'
                    }
                  >
                    {call?.outcome ?? '—'}
                  </Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Flow
                  </Text>
                  <Text fontSize="sm">{call?.flow ?? '—'}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Agent
                  </Text>
                  <Text fontSize="sm">{call?.agent_name ?? '—'}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Customer
                  </Text>
                  <Text fontSize="sm">{call?.customer_name ?? '—'}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Duration
                  </Text>
                  <Text fontSize="sm">{formatDuration(call?.callDuration)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Started
                  </Text>
                  <Text fontSize="sm">{formatTime(call?.created_at)}</Text>
                </Box>
                {call?.recording_url != null && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Recording
                    </Text>
                    <Link href={call.recording_url} color="blue.500" isExternal fontSize="sm">
                      Play Recording
                    </Link>
                  </Box>
                )}
              </SimpleGrid>

              {/* Event Timeline */}
              <Text fontWeight="semibold" mb={3}>
                Event Timeline
              </Text>
              {events.length === 0 ? (
                <Text color="gray.400" fontSize="sm">
                  No events recorded for this call.
                </Text>
              ) : (
                <Box position="relative" mb={5}>
                  {/* Vertical connecting line */}
                  <Box
                    position="absolute"
                    left="11px"
                    top="20px"
                    bottom="16px"
                    w="2px"
                    bg="gray.200"
                  />
                  {(events as CallEvent[]).map((event) => {
                    const colorScheme = EVENT_COLOR[event.event_type] ?? 'gray';
                    const dotColor = DOT_COLOR[colorScheme] ?? '#718096';
                    return (
                      <Flex key={event.id} mb={3} align="flex-start">
                        {/* Dot */}
                        <Box
                          w="8px"
                          h="8px"
                          borderRadius="full"
                          bg={dotColor}
                          flexShrink={0}
                          mt="6px"
                          mr={3}
                          zIndex={1}
                          position="relative"
                        />
                        {/* Content */}
                        <Box flex={1} minW={0}>
                          <Badge colorScheme={colorScheme} fontSize="xs" mb={1}>
                            {event.event_type.replace(/_/g, ' ')}
                          </Badge>
                          {event.participant_user_name != null && (
                            <Text fontSize="xs" fontWeight="medium">
                              {event.participant_user_name}
                            </Text>
                          )}
                          {event.participant_number != null && (
                            <Text fontSize="xs" color="gray.600">
                              {event.participant_number}
                            </Text>
                          )}
                          {event.participant_label != null && (
                            <Text fontSize="xs" color="gray.500">
                              Label: {event.participant_label}
                            </Text>
                          )}
                          {event.hunt_group_slug != null && (
                            <Text fontSize="xs" color="gray.500">
                              HG: {event.hunt_group_slug}
                            </Text>
                          )}
                          {event.reason != null && (
                            <Text fontSize="xs" color="orange.600">
                              Reason: {event.reason}
                            </Text>
                          )}
                          {event.sip_response_code != null && (
                            <Text fontSize="xs" color="red.500">
                              SIP Code: {event.sip_response_code}
                            </Text>
                          )}
                        </Box>
                        {/* Time column */}
                        <Box textAlign="right" flexShrink={0} ml={2}>
                          <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
                            {formatTimeShort(event.occurred_at)}
                          </Text>
                          {firstEventTs != null && (
                            <Text fontSize="xs" color="gray.300" whiteSpace="nowrap">
                              {getElapsedSeconds(firstEventTs, event.occurred_at)}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    );
                  })}
                </Box>
              )}

              {/* Participants */}
              {participants.length > 0 && (
                <>
                  <Divider mb={3} />
                  <Text fontWeight="semibold" mb={3}>
                    Participants
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {(participants as CdrParticipant[]).map((p) => {
                      const duration = getParticipantDuration(p);
                      return (
                        <Box key={p.id} p={2} borderRadius="md" bg="gray.50" fontSize="sm">
                          <Flex justify="space-between" align="center" mb={1}>
                            <Text fontWeight="medium">
                              {p.participant_number ?? p.participant_user_id ?? '—'}
                            </Text>
                            {p.transferred && (
                              <Badge colorScheme="blue" fontSize="xs">
                                transferred
                              </Badge>
                            )}
                          </Flex>
                          <Flex gap={4} flexWrap="wrap">
                            <Box>
                              <Text fontSize="xs" color="gray.500">
                                Joined
                              </Text>
                              <Text fontSize="xs">{formatTimeShort(p.joined_at)}</Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color="gray.500">
                                Left
                              </Text>
                              <Text fontSize="xs">
                                {p.concluded_at != null
                                  ? formatTimeShort(p.concluded_at)
                                  : 'active'}
                              </Text>
                            </Box>
                            {duration != null && (
                              <Box>
                                <Text fontSize="xs" color="gray.500">
                                  Duration
                                </Text>
                                <Text fontSize="xs">{duration}</Text>
                              </Box>
                            )}
                          </Flex>
                        </Box>
                      );
                    })}
                  </VStack>
                </>
              )}
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default CallLifecycleDrawer;
